import { useEffect, useState } from "react";
import { Loader2, Trash2, KeyRound, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConfirmAction } from "@/components/staff/ConfirmAction";

const inputCls = "rounded-none bg-black/40 border-white/15 text-white";
const EMPTY = { name: "", email: "", password: "", role: "staff" };

const StaffRow = ({ u, me, reload }) => {
  const [pwd, setPwd] = useState("");
  const resetPwd = async () => {
    try {
      await api.put(`/staff/${u.id}/password`, { password: pwd });
      toast.success(`Senha de ${u.name} alterada`);
      setPwd("");
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/staff/${u.id}`); toast.success("Conta excluída"); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="border border-white/10 p-3 space-y-2" data-testid={`staff-row-${u.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm text-white truncate">{u.name} <span className="dz-mono text-[10px] uppercase text-[#f87171] ml-1">{u.role}</span></p>
          <p className="text-xs text-zinc-500 truncate">{u.email}</p>
        </div>
        {u.id !== me.id && (
          <ConfirmAction testId={`delete-staff-${u.id}`} title="Excluir conta?" confirmLabel="Excluir"
            description={`${u.email} perderá o acesso ao painel.`} onConfirm={remove}
            trigger={<button className="text-zinc-400 hover:text-red-400" data-testid={`delete-staff-button-${u.id}`}><Trash2 size={16} /></button>} />
        )}
      </div>
      <div className="flex gap-2">
        <Input type="password" placeholder="Nova senha (mín. 8)" value={pwd} onChange={(e) => setPwd(e.target.value)} className={`${inputCls} h-8 text-xs`} data-testid={`reset-password-input-${u.id}`} />
        <button disabled={pwd.length < 8} onClick={resetPwd} data-testid={`reset-password-button-${u.id}`}
          className="flex items-center gap-1 px-3 text-xs border border-white/15 text-zinc-300 hover:text-white disabled:opacity-40"><KeyRound size={13} /> Trocar</button>
      </div>
    </div>
  );
};

export const StaffManager = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/staff").then((r) => setList(r.data)).catch((e) => toast.error(apiError(e)));

  useEffect(() => { if (open) load(); }, [open]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/staff", form);
      toast.success("Conta criada");
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121314] border-white/10 text-white rounded-none max-w-lg max-h-[90vh] overflow-y-auto" data-testid="staff-manager-dialog">
        <DialogHeader>
          <DialogTitle className="dz-heading text-2xl uppercase font-black">Equipe da staff</DialogTitle>
          <DialogDescription className="text-zinc-400">Cada membro entra com o próprio e-mail e senha.</DialogDescription>
        </DialogHeader>
        <form onSubmit={create} className="grid grid-cols-2 gap-2" data-testid="create-staff-form">
          <Input required placeholder="Nome" value={form.name} onChange={set("name")} className={inputCls} data-testid="staff-name-input" />
          <Input required type="email" placeholder="E-mail" value={form.email} onChange={set("email")} className={inputCls} data-testid="staff-email-input" />
          <Input required type="password" minLength={8} placeholder="Senha (mín. 8)" value={form.password} onChange={set("password")} className={inputCls} data-testid="staff-password-input" />
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger className={inputCls} data-testid="staff-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="staff" data-testid="staff-role-option-staff">Staff</SelectItem>
              <SelectItem value="admin" data-testid="staff-role-option-admin">Administrador</SelectItem>
            </SelectContent>
          </Select>
          <button type="submit" disabled={busy} data-testid="create-staff-submit"
            className="col-span-2 flex items-center justify-center gap-2 py-2 bg-[#c81e1e] text-white dz-heading uppercase font-bold tracking-wider disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />} Adicionar membro
          </button>
        </form>
        <div className="space-y-2" data-testid="staff-list">
          {list.map((u) => <StaffRow key={u.id} u={u} me={user} reload={load} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
};
